import { useCallback } from 'react'
import classNames from 'classnames'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { EmptyCircleIcon } from '@/atoms/Icons/EmptyCircleIcon'
import { CaptionSM, LabelSM, ParagraphSM } from '@/atoms/Text'
import { NamedColor } from '@/constants/types'
import { Label } from './Inputs'

export type OptionGroupOption = {
  label: string
  value: string
  disabled?: boolean
  disabledMessage?: string
  pillText?: string
  pillColor?: NamedColor
}

interface OptionGroupProps {
  label: string
  caption?: string
  options: OptionGroupOption[]
  limit?: number
  required?: boolean
  value: string[]
  name: string
  disabled?: boolean
  onChange: (value: string[]) => void
  wrap?: boolean
  outline?: boolean
  hasAsset?: boolean
}

export const OptionGroup = ({
  label,
  caption,
  options,
  required,
  limit = 0,
  disabled,
  onChange,
  value = [],
  name,
  wrap = true,
  outline = true,
  hasAsset,
}: OptionGroupProps) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let next = [...value]

      const index = value.indexOf(event.target.value)
      if (index > -1) {
        next.splice(index, 1)
      } else {
        next.push(event.target.value)
      }

      if (limit !== 0) {
        next = next.slice(next.length - limit)
      }

      onChange(next)
    },
    [limit, onChange, value],
  )

  const isRadio = limit === 1

  return (
    <div className={classNames('flex flex-col gap-4', { 'opacity-50 pointer-events-none': disabled })}>
      <div className="flex flex-col gap-1">
        <LabelSM>
          <Label required={required}>{label}</Label>
        </LabelSM>
        {caption && (
          <CaptionSM color="core-gray-500" className="italic">
            {caption}
          </CaptionSM>
        )}
      </div>
      <div className={classNames('flex gap-4', wrap ? 'md:flex-row md:flex-wrap flex-col' : 'flex-col')}>
        {options.map((option) => {
          const selected = hasAsset || value.includes(option.value)
          return (
            <label
              key={option.value}
              className={classNames(
                'flex items-center cursor-pointer select-none flex-row gap-2 justify-between rounded-lg px-3 py-1 !duration-[0ms] focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 focus-visible:ring-offset-transparent',
                outline && 'border-2',
                outline && {
                  'border-oxide': selected,
                  'border-core-gray-300': !selected,
                },
                option.disabled && 'opacity-50 cursor-not-allowed',
                'bg-white',
              )}
            >
              <div className="flex items-center gap-2">
                <input
                  type={isRadio ? 'radio' : 'checkbox'}
                  name={name}
                  value={option.value}
                  checked={selected}
                  onChange={handleChange}
                  className="hidden"
                  disabled={option.disabled}
                />
                <div
                  className={classNames('h-5 w-5 mt-1 shrink-0 appearance-none relative border', {
                    'bg-oxide border-oxide': selected,
                    'bg-white border-core-gray-400 border-2': !selected,
                    'rounded-full': isRadio,
                    'rounded-md': !isRadio,
                  })}
                >
                  {isRadio ? (
                    <EmptyCircleIcon className="absolute inset-0 m-auto" color1="white" color2="white" size={6} />
                  ) : (
                    <CheckIcon className="absolute inset-0 m-auto" color="white" size={18} />
                  )}
                </div>
                <div className="flex flex-col">
                  <ParagraphSM className="mt-px">{option.label}</ParagraphSM>
                  {option.disabledMessage && (
                    <ParagraphSM className="text-xs font-thin">{option.disabledMessage}</ParagraphSM>
                  )}
                </div>
              </div>
              {option.pillText && (
                <div
                  className={classNames([
                    option.pillColor ? `bg-${option.pillColor}` : `bg-lightgray`,
                    'text-white px-2 py-1 rounded-full text-[11px] my-2',
                  ])}
                >
                  {option.pillText}
                </div>
              )}
            </label>
          )
        })}
      </div>
    </div>
  )
}
